// Firebase imports
import { 
  collection, 
  addDoc, 
  getDocs, 
  getDoc,
  doc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy, 
  limit, 
  serverTimestamp,
  Timestamp 
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { DRXError, toDRXError } from '@/lib/drx-error';
import { IMessageRepository } from './interfaces';
import { isSelfPair, findExistingConversationId } from '@/lib/conversation-resolution';

// Message entity interfaces
export interface CreateMessageData {
  senderId: string;
  recipientId: string;
  subject: string;
  content: string;
  messageType: 'direct' | 'broadcast' | 'system' | 'support';
  priority: 'low' | 'normal' | 'high' | 'urgent';
  isRead: boolean;
  parentMessageId?: string;
  attachments?: string[];
  metadata?: Record<string, any>;
}

export interface UpdateMessageData {
  subject?: string;
  content?: string;
  isRead?: boolean;
  priority?: 'low' | 'normal' | 'high' | 'urgent';
  attachments?: string[];
  metadata?: Record<string, any>;
  updatedAt?: Timestamp;
}

export interface MessageFilter {
  senderId?: string;
  recipientId?: string;
  messageType?: 'direct' | 'broadcast' | 'system' | 'support';
  priority?: 'low' | 'normal' | 'high' | 'urgent';
  isRead?: boolean;
  parentMessageId?: string;
  dateFrom?: Date;
  dateTo?: Date;
}

export interface GetMessageData {
  id: string;
  senderId: string;
  recipientId: string;
  subject: string;
  content: string;
  messageType: 'direct' | 'broadcast' | 'system' | 'support';
  priority: 'low' | 'normal' | 'high' | 'urgent';
  isRead: boolean;
  parentMessageId?: string;
  attachments?: string[];
  metadata?: Record<string, any>;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

export class MessageRepository implements IMessageRepository {
  constructor(private db: any) {
    console.log('[MessageRepository] Constructor - Injected Firebase Admin DB instance:', !!this.db);
  }

  private collectionName = 'messages';
  private conversationsCollectionName = 'conversations';

  private toRepositoryError(error: unknown, code: string, devMessage: string) {
    return toDRXError(error, {
      code,
      devMessage,
    });
  }

  // Create message
  async createMessage(messageData: CreateMessageData): Promise<GetMessageData> {
    // Defense-in-depth: never persist a message addressed to its own sender.
    if (isSelfPair(messageData.senderId, messageData.recipientId)) {
      throw new DRXError({
        code: 'MESSAGE_SELF_NOT_ALLOWED',
        devMessage: `Refused self-message from ${messageData.senderId}`,
        userMessage: "You can't send a message to yourself.",
      });
    }
    try {
      const docRef = await addDoc(collection(db, this.collectionName), {
        ...messageData,
        isRead: messageData.isRead ?? false,
        priority: messageData.priority ?? 'normal',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      const docSnap = await getDoc(docRef);
      if (!docSnap.exists()) {
        throw new DRXError({
          code: 'MESSAGE_CREATE_MISSING_DOCUMENT',
          devMessage: 'Message document was missing after create',
        });
      }

      return {
        id: docRef.id,
        ...docSnap.data(),
      } as GetMessageData;
    } catch (error) {
      console.error('Error creating message:', error);
      throw this.toRepositoryError(error, 'MESSAGE_CREATE_FAILED', 'Failed to create message');
    }
  }

  // Get message by ID
  async getMessageById(messageId: string): Promise<GetMessageData | null> {
    try {
      const docRef = doc(db, this.collectionName, messageId);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        return null;
      }

      return {
        id: docSnap.id,
        ...docSnap.data(),
      } as GetMessageData;
    } catch (error) {
      console.error('Error getting message by ID:', error);
      throw this.toRepositoryError(error, 'MESSAGE_FETCH_FAILED', `Failed to get message ${messageId}`);
    }
  }

  // Get messages with filters
  async getMessages(filter: MessageFilter = {}, limitCount?: number): Promise<GetMessageData[]> {
    try {
      let q = collection(db, this.collectionName);

      // Build query with filters
      if (filter.senderId) {
        q = query(q, where('senderId', '==', filter.senderId));
      }
      if (filter.recipientId) {
        q = query(q, where('recipientId', '==', filter.recipientId));
      }
      if (filter.messageType) {
        q = query(q, where('messageType', '==', filter.messageType));
      }
      if (filter.priority) {
        q = query(q, where('priority', '==', filter.priority));
      }
      if (filter.isRead !== undefined) {
        q = query(q, where('isRead', '==', filter.isRead));
      }
      if (filter.parentMessageId) {
        q = query(q, where('parentMessageId', '==', filter.parentMessageId));
      }

      // Add ordering and limit
      q = query(q, orderBy('createdAt', 'desc'));
      if (limitCount) {
        q = query(q, limit(limitCount));
      }

      const querySnapshot = await getDocs(q);
      let messages: GetMessageData[] = [];

      querySnapshot.forEach((doc) => {
        messages.push({
          id: doc.id,
          ...doc.data(),
        } as GetMessageData);
      });

      // Apply date filters (since Firestore doesn't support range queries with other filters easily)
      if (filter.dateFrom) {
        messages = messages.filter(message =>
          message.createdAt.toDate() >= filter.dateFrom!
        );
      }
      if (filter.dateTo) {
        messages = messages.filter(message =>
          message.createdAt.toDate() <= filter.dateTo!
        );
      }

      return messages;
    } catch (error) {
      console.error('Error getting messages:', error);
      throw this.toRepositoryError(error, 'MESSAGES_FETCH_FAILED', 'Failed to get messages');
    }
  }

  // Update message
  async updateMessage(messageId: string, updateData: UpdateMessageData): Promise<GetMessageData> {
    try {
      const docRef = doc(db, this.collectionName, messageId);
      await updateDoc(docRef, {
        ...updateData,
        updatedAt: serverTimestamp(),
      });

      const docSnap = await getDoc(docRef);
      if (!docSnap.exists()) {
        throw new DRXError({
          code: 'MESSAGE_NOT_FOUND_AFTER_UPDATE',
          devMessage: `Message ${messageId} not found after update`,
          userMessage: 'Message not found.',
          level: 'warning',
        });
      }

      return {
        id: docSnap.id,
        ...docSnap.data(),
      } as GetMessageData;
    } catch (error) {
      console.error('Error updating message:', error);
      throw this.toRepositoryError(error, 'MESSAGE_UPDATE_FAILED', `Failed to update message ${messageId}`);
    }
  }

  // Delete message
  async deleteMessage(messageId: string): Promise<void> {
    try {
      const docRef = doc(db, this.collectionName, messageId);
      await deleteDoc(docRef);
    } catch (error) {
      console.error('Error deleting message:', error);
      throw this.toRepositoryError(error, 'MESSAGE_DELETE_FAILED', `Failed to delete message ${messageId}`);
    }
  }

  // Get messages by type
  async getMessagesByType(messageType: 'direct' | 'broadcast' | 'system' | 'support', limitCount?: number): Promise<GetMessageData[]> {
    try {
      const filter: MessageFilter = { messageType: messageType };
      return this.getMessages(filter, limitCount);
    } catch (error) {
      console.error('Error getting messages by type:', error);
      throw this.toRepositoryError(error, 'MESSAGES_BY_TYPE_FETCH_FAILED', `Failed to get messages by type ${messageType}`);
    }
  }

  // Get messages by priority
  async getMessagesByPriority(priority: 'low' | 'normal' | 'high' | 'urgent', limitCount?: number): Promise<GetMessageData[]> {
    try {
      const filter: MessageFilter = { priority };
      return this.getMessages(filter, limitCount);
    } catch (error) {
      console.error('Error getting messages by priority:', error);
      throw this.toRepositoryError(error, 'MESSAGES_BY_PRIORITY_FETCH_FAILED', `Failed to get messages by priority ${priority}`);
    }
  }

  // Get unread messages
  async getUnreadMessages(limitCount?: number): Promise<GetMessageData[]> {
    try {
      const filter: MessageFilter = { isRead: false };
      return this.getMessages(filter, limitCount);
    } catch (error) {
      console.error('Error getting unread messages:', error);
      throw this.toRepositoryError(error, 'MESSAGES_UNREAD_FETCH_FAILED', 'Failed to get unread messages');
    }
  }

  // Get user inbox messages
  async getUserInbox(userId: string, limitCount?: number): Promise<GetMessageData[]> {
    try {
      const filter: MessageFilter = { recipientId: userId };
      return this.getMessages(filter, limitCount);
    } catch (error) {
      console.error('Error getting user inbox:', error);
      throw this.toRepositoryError(error, 'MESSAGES_INBOX_FETCH_FAILED', `Failed to get inbox for user ${userId}`);
    }
  }

  // Get messages by owner (sender)
  async getMessagesByOwner(ownerId: string, limitCount?: number): Promise<GetMessageData[]> {
    try {
      return this.getUserSentMessages(ownerId, limitCount);
    } catch (error) {
      console.error('Error getting messages by owner:', error);
      throw this.toRepositoryError(error, 'MESSAGES_BY_OWNER_FETCH_FAILED', `Failed to get messages for owner ${ownerId}`);
    }
  }

  // Get all messages (admin only) or owner's messages
  async getMessagesForUser(userId: string, isAdmin: boolean = false, limitCount?: number): Promise<GetMessageData[]> {
    try {
      if (isAdmin) {
        // Admin sees all messages
        return this.getMessages({}, limitCount);
      } else {
        // Regular user sees only their own messages (sent + received)
        const [sentMessages, receivedMessages] = await Promise.all([
          this.getUserSentMessages(userId, limitCount),
          this.getUserInbox(userId, limitCount)
        ]);

        const allMessages = [...sentMessages, ...receivedMessages];
        return allMessages.slice(0, limitCount || allMessages.length);
      }
    } catch (error) {
      console.error('Error getting messages for user:', error);
      throw this.toRepositoryError(error, 'MESSAGES_FOR_USER_FETCH_FAILED', `Failed to get messages for user ${userId}`);
    }
  }

  // Get all messages (admin only)
  async getAllMessages(limitCount?: number): Promise<GetMessageData[]> {
    try {
      return this.getMessages({}, limitCount);
    } catch (error) {
      console.error('Error getting all messages:', error);
      throw this.toRepositoryError(error, 'MESSAGES_ALL_FETCH_FAILED', 'Failed to get all messages');
    }
  }

  // Get user sent messages
  async getUserSentMessages(userId: string, limitCount?: number): Promise<GetMessageData[]> {
    try {
      const filter: MessageFilter = { senderId: userId };
      return this.getMessages(filter, limitCount);
    } catch (error) {
      console.error('Error getting user sent messages:', error);
      throw this.toRepositoryError(error, 'MESSAGES_SENT_FETCH_FAILED', `Failed to get sent messages for user ${userId}`);
    }
  }

  // Get unread messages count
  async getUnreadMessagesCount(userId: string): Promise<number> {
    try {
      const q = query(
        collection(db, this.collectionName),
        where('recipientId', '==', userId),
        where('isRead', '==', false)
      );

      const querySnapshot = await getDocs(q);
      return querySnapshot.size;
    } catch (error) {
      console.error('Error getting unread messages count:', error);
      throw this.toRepositoryError(error, 'MESSAGES_UNREAD_COUNT_FAILED', `Failed to get unread message count for user ${userId}`);
    }
  }

  // Mark message as read
  async markMessageAsRead(messageId: string): Promise<GetMessageData> {
    try {
      return this.updateMessage(messageId, { isRead: true });
    } catch (error) {
      console.error('Error marking message as read:', error);
      throw this.toRepositoryError(error, 'MESSAGE_MARK_READ_FAILED', `Failed to mark message ${messageId} as read`);
    }
  }

  // Mark all messages as read for user
  async markAllMessagesAsRead(userId: string): Promise<void> {
    try {
      const q = query(
        collection(db, this.collectionName),
        where('recipientId', '==', userId),
        where('isRead', '==', false)
      );

      const querySnapshot = await getDocs(q);
      const updatePromises = querySnapshot.docs.map(doc =>
        updateDoc(doc.ref, {
          isRead: true,
          updatedAt: serverTimestamp(),
        })
      );

      await Promise.all(updatePromises);
    } catch (error) {
      console.error('Error marking all messages as read:', error);
      throw this.toRepositoryError(error, 'MESSAGES_MARK_ALL_READ_FAILED', `Failed to mark all messages as read for user ${userId}`);
    }
  }

  // Get conversation between two users
  async getConversation(userId1: string, userId2: string, limitCount?: number): Promise<GetMessageData[]> {
    try {
      // Get messages where user1 is sender and user2 is recipient
      const sentMessages = await this.getMessages({
        senderId: userId1,
        recipientId: userId2,
      });

      // Get messages where user2 is sender and user1 is recipient
      const receivedMessages = await this.getMessages({
        senderId: userId2,
        recipientId: userId1,
      });

      // Combine and sort by creation date
      const allMessages = [...sentMessages, ...receivedMessages]
        .sort((a, b) => b.createdAt.toDate().getTime() - a.createdAt.toDate().getTime());

      return limitCount ? allMessages.slice(0, limitCount) : allMessages;
    } catch (error) {
      console.error('Error getting conversation:', error);
      throw this.toRepositoryError(error, 'CONVERSATION_FETCH_FAILED', `Failed to get conversation between ${userId1} and ${userId2}`);
    }
  }

  // Send broadcast message
  async sendBroadcastMessage(senderId: string, subject: string, content: string, priority: 'low' | 'normal' | 'high' | 'urgent' = 'normal'): Promise<GetMessageData[]> {
    try {
      // This would typically get all users and send to each
      // For now, we'll just create a sample broadcast message
      const messageData: CreateMessageData = {
        senderId: senderId,
        recipientId: 'all', // Special recipient for broadcasts
        subject,
        content,
        messageType: 'broadcast',
        priority,
        isRead: false,
      };

      const message = await this.createMessage(messageData);
      return [message];
    } catch (error) {
      console.error('Error sending broadcast message:', error);
      throw this.toRepositoryError(error, 'MESSAGE_BROADCAST_FAILED', `Failed to send broadcast message from ${senderId}`);
    }
  }

  // Get message thread (replies to a message)
  async getMessageThread(parentMessageId: string): Promise<GetMessageData[]> {
    try {
      const filter: MessageFilter = { parentMessageId: parentMessageId };
      const replies = await this.getMessages(filter);

      // Get the parent message
      const parentMessage = await this.getMessageById(parentMessageId);
      if (!parentMessage) {
        return replies;
      }

      // Return parent message followed by replies
      return [parentMessage, ...replies];
    } catch (error) {
      console.error('Error getting message thread:', error);
      throw this.toRepositoryError(error, 'MESSAGE_THREAD_FETCH_FAILED', `Failed to get message thread ${parentMessageId}`);
    }
  }

  // ===== CONVERSATION METHODS (absorbed from messages.service.ts) =====

  async getOrCreateConversation(
    userId: string,
    userName: string,
    recipientId: string,
    recipientName: string,
    jobId?: string,
    jobTitle?: string,
    userAvatar?: string,
    recipientAvatar?: string
  ): Promise<string> {
    // Self-conversation guard — the true chokepoint. When userId === recipientId
    // the participant pair collapses ([jen, jen]) and the "which of my
    // conversations includes the recipient?" lookup below matches EVERY
    // conversation the user is in — so a self-message got dumped into an
    // arbitrary existing thread (the reported "I messaged myself and it went to
    // Brooke" bug). A self-conversation is never valid, so refuse it here rather
    // than let the lookup pick a wrong thread.
    if (isSelfPair(userId, recipientId)) {
      throw new DRXError({
        code: 'CONVERSATION_SELF_NOT_ALLOWED',
        devMessage: `Refused self-conversation for user ${userId}`,
        userMessage: "You can't start a conversation with yourself.",
      });
    }
    try {
      const q = query(
        collection(db, this.conversationsCollectionName),
        where('participants', 'array-contains', userId)
      );
      const snapshot = await getDocs(q);

      // Match the FIRST existing thread that also contains the recipient (pure,
      // unit-tested — see conversation-resolution.ts).
      const existingId = findExistingConversationId(
        snapshot.docs.map((d) => ({ id: d.id, participants: d.data().participants })),
        recipientId,
      );

      if (existingId) return existingId;

      const newConversation: Record<string, any> = {
        participants: [userId, recipientId],
        participantNames: { [userId]: userName, [recipientId]: recipientName },
        participantAvatars: { [userId]: userAvatar || '', [recipientId]: recipientAvatar || '' },
        unreadCount: { [userId]: 0, [recipientId]: 0 },
        ...(jobId && { jobId }),
        ...(jobTitle && { jobTitle }),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      const docRef = await addDoc(collection(db, this.conversationsCollectionName), newConversation);
      return docRef.id;
    } catch (error) {
      throw new DRXError({
        code: 'CONVERSATION_CREATE_FAILED',
        devMessage: `Failed to get/create conversation between ${userId} and ${recipientId}`,
        cause: error,
      });
    }
  }

  async getUserConversations(userId: string): Promise<any[]> {
    try {
      const q = query(
        collection(db, this.conversationsCollectionName),
        where('participants', 'array-contains', userId),
        orderBy('lastMessageTime', 'desc')
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    } catch (error) {
      // Fallback without ordering if index doesn't exist
      try {
        const q = query(
          collection(db, this.conversationsCollectionName),
          where('participants', 'array-contains', userId)
        );
        const snapshot = await getDocs(q);
        const conversations = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
        return conversations.sort((a: any, b: any) => {
          const aTime = a.lastMessageTime?.seconds || 0;
          const bTime = b.lastMessageTime?.seconds || 0;
          return bTime - aTime;
        });
      } catch (fallbackError) {
        throw new DRXError({
          code: 'CONVERSATIONS_FETCH_FAILED',
          devMessage: `Failed to fetch conversations for user ${userId}`,
          cause: fallbackError,
        });
      }
    }
  }

  async getConversationMessages(conversationId: string): Promise<GetMessageData[]> {
    try {
      const q = query(
        collection(db, this.collectionName),
        where('conversationId', '==', conversationId),
        orderBy('createdAt', 'asc')
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(d => ({ id: d.id, ...d.data() } as GetMessageData));
    } catch (error) {
      // Fallback without ordering
      try {
        const q = query(
          collection(db, this.collectionName),
          where('conversationId', '==', conversationId)
        );
        const snapshot = await getDocs(q);
        const messages = snapshot.docs.map(d => ({ id: d.id, ...d.data() } as GetMessageData));
        return messages.sort((a: any, b: any) => {
          const aTime = a.createdAt?.seconds || 0;
          const bTime = b.createdAt?.seconds || 0;
          return aTime - bTime;
        });
      } catch (fallbackError) {
        throw new DRXError({
          code: 'CONVERSATION_MESSAGES_FETCH_FAILED',
          devMessage: `Failed to fetch messages for conversation ${conversationId}`,
          cause: fallbackError,
        });
      }
    }
  }

  /** Connect/job thread message document (uses `read` field, not `isRead`). */
  async createThreadMessage(messageFields: Record<string, unknown>): Promise<string> {
    try {
      const docRef = await addDoc(collection(db, this.collectionName), {
        ...messageFields,
      });
      return docRef.id;
    } catch (error) {
      throw this.toRepositoryError(error, 'THREAD_MESSAGE_CREATE_FAILED', 'Failed to create thread message');
    }
  }

  async updateConversationDocument(
    conversationId: string,
    data: Record<string, unknown>,
  ): Promise<void> {
    try {
      await updateDoc(doc(db, this.conversationsCollectionName, conversationId), data);
    } catch (error) {
      throw this.toRepositoryError(
        error,
        'CONVERSATION_UPDATE_FAILED',
        `Failed to update conversation ${conversationId}`,
      );
    }
  }

  /**
   * Count unread messages in a 1:1 conversation. Called by the SENDER
   * after writing a new message so the recipient's `unreadCount` on the
   * conversation doc can be refreshed.
   *
   * The Firestore rule on /messages requires
   *   senderId == auth.uid OR recipientId == auth.uid
   * and the rules engine validates this STATICALLY from the query
   * filters — it can't introspect doc contents to prove the OR clause.
   * A query of `(conversationId, recipientId == otherUid, read == false)`
   * fails the static check from the sender's perspective: every returned
   * doc would have `senderId == auth.uid`, but Firestore can't prove that
   * from the filters, so the listener / getDocs is rejected with
   * permission-denied. Adding `senderId == auth.uid` to the query both
   * narrows the result to the right set (messages the sender wrote that
   * the recipient hasn't read) AND lets the rule pass statically.
   */
  async countUnreadMessagesInConversation(
    conversationId: string,
    senderId: string,
    recipientId: string,
  ): Promise<number> {
    try {
      const q = query(
        collection(db, this.collectionName),
        where('conversationId', '==', conversationId),
        where('senderId', '==', senderId),
        where('recipientId', '==', recipientId),
        where('read', '==', false),
      );
      const snapshot = await getDocs(q);
      return snapshot.size;
    } catch (error) {
      throw this.toRepositoryError(
        error,
        'THREAD_UNREAD_COUNT_FAILED',
        'Failed to count unread messages in conversation',
      );
    }
  }

  /**
   * Fetch every job-application-style message addressed to a given job.
   * Caller is always the JOB CREATOR (the recipient on every applicant
   * message), so we filter on `recipientId == jobCreatorId` to satisfy
   * the messages rule (`senderId == auth.uid OR recipientId == auth.uid`).
   * Without that filter Firestore rejects the query at static rule-check
   * time even though the caller would have been allowed to read each
   * doc individually.
   */
  async getJobApplicationMessages(
    jobId: string,
    jobCreatorId: string,
  ): Promise<Array<Record<string, unknown> & { id: string }>> {
    try {
      const q = query(
        collection(db, this.collectionName),
        where('jobId', '==', jobId),
        where('recipientId', '==', jobCreatorId),
        where('jobType', '==', 'existing'),
        where('messageType', '==', 'application'),
      );
      const snapshot = await getDocs(q);
      const applications = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      return applications.sort((a, b) => {
        const aTime =
          a.createdAt && typeof a.createdAt === 'object' && 'seconds' in (a.createdAt as object)
            ? (a.createdAt as { seconds: number }).seconds * 1000
            : typeof a.createdAt === 'string'
              ? new Date(a.createdAt).getTime()
              : 0;
        const bTime =
          b.createdAt && typeof b.createdAt === 'object' && 'seconds' in (b.createdAt as object)
            ? (b.createdAt as { seconds: number }).seconds * 1000
            : typeof b.createdAt === 'string'
              ? new Date(b.createdAt).getTime()
              : 0;
        return bTime - aTime;
      });
    } catch (error) {
      throw this.toRepositoryError(
        error,
        'JOB_APPLICATION_MESSAGES_FETCH_FAILED',
        `Failed to fetch job applications for job ${jobId}`,
      );
    }
  }

  async patchThreadMessage(messageId: string, patch: Record<string, unknown>): Promise<void> {
    try {
      await updateDoc(doc(db, this.collectionName, messageId), {
        ...patch,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      throw this.toRepositoryError(error, 'THREAD_MESSAGE_PATCH_FAILED', `Failed to update message ${messageId}`);
    }
  }

  /**
   * "Delete a conversation" from a single user's perspective. Firestore
   * rules constrain what a normal user can actually drop:
   *   • messages: only deletable by the sender (rule on /messages)
   *   • conversation doc: admin-only delete (rule on /conversations)
   *
   * So this method can only delete the caller's own outgoing messages
   * and tag the conversation as deleted-for-them via `deletedFor`. The
   * other party still sees their copy of the thread until they delete
   * it themselves. The previous version queried `where(conversationId)`
   * with no sender/recipient filter, which Firestore rejected at static
   * rule-check time and surfaced as permission-denied.
   */
  async deleteConversationCascade(
    conversationId: string,
    callerUserId: string,
  ): Promise<void> {
    try {
      const ownMessagesQuery = query(
        collection(db, this.collectionName),
        where('conversationId', '==', conversationId),
        where('senderId', '==', callerUserId),
      );
      const messagesSnapshot = await getDocs(ownMessagesQuery);
      await Promise.all(messagesSnapshot.docs.map((d) => deleteDoc(d.ref)));

      // Mark the conversation as hidden for this user instead of deleting
      // the doc. The conversation rule blocks non-admin deletes; updates
      // are allowed when the caller stays in `participants`.
      await updateDoc(doc(db, this.conversationsCollectionName, conversationId), {
        [`deletedFor.${callerUserId}`]: serverTimestamp(),
        [`unreadCount.${callerUserId}`]: 0,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      throw this.toRepositoryError(
        error,
        'CONVERSATION_DELETE_FAILED',
        `Failed to delete conversation ${conversationId}`,
      );
    }
  }

  async markConversationMessagesAsRead(conversationId: string, userId: string): Promise<void> {
    try {
      const q = query(
        collection(db, this.collectionName),
        where('conversationId', '==', conversationId),
        where('recipientId', '==', userId),
        where('read', '==', false)
      );
      const snapshot = await getDocs(q);
      const updates = snapshot.docs.map(d =>
        updateDoc(d.ref, { read: true, updatedAt: serverTimestamp() })
      );
      await Promise.all(updates);

      await updateDoc(doc(db, this.conversationsCollectionName, conversationId), {
        [`unreadCount.${userId}`]: 0,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      throw new DRXError({
        code: 'MESSAGES_MARK_READ_FAILED',
        devMessage: `Failed to mark messages as read in conversation ${conversationId}`,
        cause: error,
      });
    }
  }

}
